import React, { useCallback, useRef, useState } from 'react';
import { useRequest } from 'ahooks';

/**
 * @typedef {import('../../../../types/provider').Provider} Provider
 */
import { fetchFirstProviderByNpi } from '../../../../utils/serviceCalls/provider';
import { isValidNpi } from '../../../../utils/validation';
import {
  ClearIcon,
  LoadingIndicator,
  SearchSubmitIcon,
} from '../../../Shared/SearchCombobox/Icons';
import { VALIDATION_MESSAGES } from '../../constants';
import { FIELDS, INPUT_WITH_ICON_PADDING_CLASSES } from '../constants';
import FieldWrapper from './FieldWrapper';
import { useFormContextSafe, useFormField } from './hooks';

/**
 * Hook to search for provider by NPI as you type.
 *
 * Key behaviors:
 * - Debounces API calls by 300ms
 * - Shows "busy" state during debounce AND loading (via dirty tracking)
 * - Clears previous results immediately when input changes (via onBefore)
 * - Uses ref for API errors to avoid stale closure in validate()
 *
 * @param {string|null} npi - 10-digit NPI number (null if invalid format)
 * @param {boolean} disabled - Whether search should be skipped
 * @param {(provider: Provider|null) => void} onProviderUpdate - Callback when provider changes
 */
function useNpiSearch(npi, disabled = false, onProviderUpdate) {
  const { trigger } = useFormContextSafe();

  const apiErrorRef = useRef(/** @type {string|null} */ (null));

  // Track last searched value to compute "dirty" state
  // Initialize to null (not npi) to indicate "no search completed yet"
  const [lastSearchedNpi, setLastSearchedNpi] = useState(
    /** @type {string|null} */ (null),
  );

  // Dirty = input changed since last search completed
  const dirty = npi !== lastSearchedNpi;
  const shouldSearch = !disabled && !!npi;

  /** Clears API error immediately - call on input change */
  const clearApiError = useCallback(() => {
    apiErrorRef.current = null;
  }, []);

  const { loading, data: provider } = useRequest(
    async () => {
      if (!npi || disabled) {
        return null;
      }

      try {
        const { provider: foundProvider, errorCode: fetchErrorCode } =
          await fetchFirstProviderByNpi(npi);

        // Handle all errors first
        if (fetchErrorCode) {
          apiErrorRef.current = VALIDATION_MESSAGES.SEARCH_ERROR;
          onProviderUpdate(null);
          return null;
        }

        if (!foundProvider) {
          // Provider not found
          apiErrorRef.current = VALIDATION_MESSAGES.NPI_NOT_FOUND;
          onProviderUpdate(null);
          return null;
        }

        apiErrorRef.current = null;
        onProviderUpdate(foundProvider);
        return foundProvider;
      } catch {
        apiErrorRef.current = VALIDATION_MESSAGES.SEARCH_ERROR;
        onProviderUpdate(null);
        return null;
      }
    },
    {
      refreshDeps: [npi, disabled],
      debounceWait: 300,
      ready: shouldSearch,
      onBefore: () => {
        // Immediately clear previous results when new search starts
        apiErrorRef.current = null;
        onProviderUpdate(null);
      },
      onFinally: () => {
        setLastSearchedNpi(npi ?? '');
        trigger(FIELDS.npi);
      },
    },
  );

  // Busy = should search AND (input changed OR request in flight)
  // This shows spinner during debounce period, not just during loading
  const busy = shouldSearch && (dirty || loading);

  return {
    busy,
    provider: provider || null,
    apiErrorRef,
    clearApiError,
  };
}

/**
 * NPI input field with search-as-you-type functionality.
 *
 * Features:
 * - Validates NPI format (10 digits)
 * - Searches for provider via API with 300ms debounce
 * - Shows loading indicator during debounce and API call
 * - Shows clear button when provider is found
 * - Notifies parent immediately when input changes (clears previous result)
 *
 * @param {Object} props
 * @param {(provider: Provider|null) => void} props.onProviderUpdate - Callback when provider changes
 * @param {boolean} [props.disabled] - Whether the field is disabled
 */
export default function NpiField({ onProviderUpdate, disabled }) {
  const {
    register,
    errorMessage,
    handleKeyDown,
    currentValue,
    clearField,
    validateField,
  } = useFormField(FIELDS.npi, disabled);

  // Only search if value is a valid NPI format
  const npiToSearch = isValidNpi(currentValue || '') ? currentValue : null;

  const { busy, provider, apiErrorRef, clearApiError } = useNpiSearch(
    npiToSearch,
    disabled,
    onProviderUpdate,
  );

  const { onChange: rhfOnChange, ...restRegistration } = register(FIELDS.npi, {
    validate: (val = '') => {
      if (disabled) return true;
      if (!val?.trim()) return true; // Optional field

      if (!isValidNpi(val)) return VALIDATION_MESSAGES.NPI_INVALID;

      // Read from ref to avoid stale closure (TR-3)
      if (apiErrorRef.current) return apiErrorRef.current;

      return true;
    },
  });

  const handleChange = (e) => {
    // Clear API error immediately on any input change
    // (onBefore only fires after debounce wait)
    clearApiError();
    rhfOnChange(e);
    // Trigger validation to clear any displayed error
    validateField();
  };

  const handleClearClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearField();
    onProviderUpdate(null);
  };

  const handleSearchClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    validateField();
  };

  // Show clear button when provider found and not currently searching
  const showClearButton = provider && !busy;

  return (
    <FieldWrapper label="NPI" errorMessage={errorMessage}>
      <div className="relative">
        <input
          {...restRegistration}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type="text"
          inputMode="numeric"
          maxLength={10}
          placeholder="Provider's NPI"
          className={INPUT_WITH_ICON_PADDING_CLASSES}
          aria-invalid={!!errorMessage}
          disabled={disabled}
        />

        {showClearButton ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleClearClick}
            className="absolute inset-y-0 right-0 flex items-center pr-3 hover:opacity-70 cursor-pointer"
            title="Clear input"
            disabled={disabled}
          >
            <ClearIcon />
          </button>
        ) : busy ? (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <LoadingIndicator />
          </div>
        ) : (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleSearchClick}
            className="absolute inset-y-0 right-0 flex items-center pr-3 hover:opacity-70 cursor-pointer"
            title="Search for provider"
            disabled={disabled}
          >
            <SearchSubmitIcon className="h-5 w-5 opacity-40" />
          </button>
        )}
      </div>
    </FieldWrapper>
  );
}
